import { CooldownResolver } from 'web/engine/cooldowns/core-resolver'

/**
 * Generic ID type
 */
export type Id = { id: string }

/**
 * Generic type for entities with children
 */
export type Parent = { childId?: string }

/**
 * Holds optional props for generic game data. These props use the underscore
 * syntax to differentiate them from normal props which might be holding display
 * data.
 *
 * These props should NEVER be directly displayed. We use them purely as
 * data-carriers.
 */
export type GameData = {
    /**
     * The ID used to identify a spell or talent. Useful for mapping icons or
     * making addon exports.
     */
    game_id?: number

    /**
     * Name of the icon used to represent this object. Can be found on Wowhead.
     */
    icon_name?: string

    /**
     * Used when creating Wowhead links to create a more specific
     * tooltip, for example when referring to different ranks of a
     * talent.
     */
    link_suffix?: string
}

/**
 * Generic type for select-input options
 */
export type SelectOption = { value: string; label: string }

/**
 * Generic type for an optiongroup structure
 */
export type SelectOptionGroup = {
    label: string
    items: SelectOption[]

    /**
     * If false, these items should never appear in dropdowns that act
     * as inputs to writeable data. They are allowed to appear in
     * dropdowns where there's data attached to them already.
     *
     * Concrete example: If a fight has an outline, it should still be
     * selectable for viewing, but new fight outlines should not be
     * allowed to select this fight and save.
     */
    isSelectable: boolean

    imgUrl?: string
}

/**
 * Generic type for tracking modification dates
 */
export type Dated = { createdAt: string; updatedAt: string }

export type SpecMap = { [id: string]: Spec }
export type SpellMap = { [id: string]: Spell }
export type TalentMap = { [id: string]: Talent }
export type ModifierMap = { [id: string]: Modifier }

export type PatchInfo = {
    /**
     * The specific build version. Use the following format:
     *
     * <patch>_<auto-increment>, i.g. "830_1", "830_2", etc...
     *
     * NOTICE - It's important that this value is incremental. It's used to
     * figure out what a newer version has been released.
     */
    buildVersion: string

    /**
     * TODO: NYI - If true, we should run pruning, otherwise we should
     * update everyone to this? Why even have this then? Just
     * overwrite the current build with a new deploy? The trick is
     * more about defining what constitutes a "breaking" change.
     *
     * Adding spells or tweaking display values (name, game_id, etc)
     * should be fine, but any removal or cooldown modifications
     * should be considered breaking, as they'll mess up whatever
     * setup they're used in.
     */
    breaking?: boolean
}

/**
 * Outlined data about a specific game build.
 */
export type BuildData = {

    patchInfo: PatchInfo

    /**
     * Optional message aimed at the user that explains the delta between the
     * previous build and this one.
     *
     * Be specific here, we'll explain the general idea of setups becoming
     * read-only and roster players being pruned elsewhere.
     */
    changelog?: string[]

    /**
     * Contains a dictionary of available specs.
     *
     * NOTE — This dict is not guaranteed to contain all specs. Specs can come
     * and go from this dictionary from build to build. If a spec loses all of
     * its assignable spells, they will be removed from this dictionary.
     */
    specsById: SpecMap

    spellsById: SpellMap
    talentsById: TalentMap
    modifiersById: ModifierMap

    // TODO: Has a _very_ questionable existence. Was created as a work-around
    // for accessing SpecIds after moving them into the builds instead of being
    // general
    /**
     * Returns the spec with the given name. If no spec is found, an error is
     * thrown.
     */
    SpecByName: (classId: string, fullClassName: string) => Spec

    /**
     * Returns the spec for the given ID. Throws an error if none can be found.
     *
     * If you need to do a presence check, use the dictionaries in this object
     * instead.
     */
    Spec: (specId: string) => Spec

    /**
     * Returns the spell for the given ID. Throws an error if none can be found.
     *
     * If you need to do a presence check, use the dictionaries in this object
     * instead.
     */
    Spell: (spellId: string) => Spell

    /**
     * Returns the talent for the given ID. Throws an error if none can be
     * found.
     *
     * If you need to do a presence check, use the dictionaries in this object
     * instead.
     */
    Talent: (talentId: string) => Talent

    /**
     * Returns the modifier for the given ID. Throws an error if none can be
     * found.
     *
     * If you need to do a presence check, use the dictionaries in this object
     * instead.
     */
    Modifier: (modifierId: string) => Modifier

    /**
     * ### This should only be accessed by `resolveSpellCooldown`.
     *
     * Returns a cooldown resolver for the given spec which can be used to
     * calculate the cooldown of a given spell/player combination.
     *
     * Returns undefined if no resolver is defined for the given spec.
     */
    //
    // TODO: This signature and implementation is hard to understand, what are
    // you doing here buddy? We take in a spellId, then we create a closure that
    // still request the spell again? Can probably be simplified.
    //
    __CooldownResolver: (spellId: string) => CooldownResolver

}

/**
 * TS Model for the build data we generate from the build-admin.
 */
export type GeneratedBuildData = {
    patchInfo: PatchInfo
    changelog?: string[]

    specsById: SpecMap
    spellsById: SpellMap
    talentsById: TalentMap
    modifiersById: ModifierMap

    __SPELL_ID: any
    __TALENT_ID: any
    __MODIFIER_ID: any
}

// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#
//
// Player setup
//
// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#

export type RosterPlayer = Id & {
    patchInfo: PatchInfo

    name: string
    specId: string
    talentIds: string[]
    modifierIds: string[]
    overrides: SpellOverride[]
}

/**
 * Used to have its own properties, is now just a 1:1 with a
 * RosterPlayer. Notice however that we don't create any connections
 * between them.
 *
 * The ID field in each model just represents a unique instance.
 *
 * When a roster player is added to a setup, we just clone the entire
 * instance and reroll the ID.
 *
 * For detecting players that are already added, we rely solely on a
 * name & spec check. This is because after a roster player has been
 * added to the setup, they might get renamed to another player. At
 * which point it would not make any sense to exclude the previous
 * player from the add-from-roster list.
 */
export type FightPlayer = RosterPlayer

export type SpellOverride = Id & {
    spellId: string
    cooldown: number

    /**
     * If true, no talents or modifiers will be calculated for this spell
     */
    ignoreModifiers: boolean
}

export enum SpecRole {
    Tank = "Tank",
    Healer = "Healer",
    Melee = "Melee DPS",
    Ranged = "Ranged DPS",
}

export type Spec = Id & GameData & {
    classId: string
    name: string
    shortName: string
    role: SpecRole
    spellIds: string[]
    talentIds: string[]
}

export type Talent = Id & GameData & {
    name: string
    shortName: string
    specId: string
}

export type Spell = Id & GameData & {
    name: string
    shortName: string
    cooldown: number

    /**
     * If this spell is locked behind a talent, set that talent ID here
     */
    talentId?: string

    /**
     * If this spell is locked behind a modifier, set that modifier ID here
     */
    modifierId?: string

    /**
     * Defines a list of modifiers this spell needs to take into account when
     * calculating its cooldown
     */
    affectedByModifierIds: string[]

    /**
     * If set, means the player is expected to have a talented spells that
     * overwrites this spell. Making this spell effectively disabled until the
     * talent is un-selected.
     */
    replacedByTalentId?: string
}

// TODO: Be careful about this type, I'm not sure how to best design it to be
// flexible
export type Modifier = Id & GameData & {
    name: string
    shortName: string
    eligibleSpecIds: string[]
    meta: any // Some sort of generic carrier for the modification data
}

// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#
//
// Timeline
//
// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#

export enum FightEventFlag {
    Movement = "movement",
    Burst = "burst",
    Rot = "rot",
    Spread = "spread",
    Emphasize = "emphasize",
    Muted = "mute",
}

/**
 * Helper type to group all the parts of a fight
 */
export type FullFight = {
    fight: Fight
    segments: FightSegment[]
    events: FightEvent[]
}

export type Fight = Id & Dated & {
    name: string
    note: string
    categoryId: string
}

export type FightSegment = Id & Parent & {
    fightId: string
    userCreated: boolean

    name: string

    /**
     * ERT timer condition string
     */
    condition?: string

    /**
     * Offset in seconds from fight start
     */
    offset: number
}

export type FightEvent = Id & {
    fightSegmentId: string
    userCreated: boolean

    /**
     * Offset in seconds from fight start
     */
    offset: number

    flags: FightEventFlag[]

    name: string
    comment: string
}

/**
 * We need an identifiable type for this in order to shake it out of
 * the fight data when we're done editing.
 */
export type SetupFightEvent = FightEvent & {
    setupId: string
}

export type FightSetup = Id & Dated & {
    patchInfo: PatchInfo

    name: string
    fightId: string

    fightPlayers: FightPlayer[]

    spellAssignments: SpellAssignment[]
    teamAssignments: TeamAssignment[]

    /**
     * These events live in the outline, but are owned by this
     * particular setup. They are still subject to all the normal
     * rules of an outline.
     */
    // TODO: Think long and hard about naming here, you'll break serialization
    setupFightEvents: SetupFightEvent[]
}

// TODO: Rename to GenericAssignmentType
// TODO: Think long and hard about naming here, you'll break serialization
export enum TeamAssignmentType {
    healthstone,
    personal,
}

export type TeamAssignment = {
    fightEventId: string
    eventOffset: number
    type: TeamAssignmentType
}


export type SpellAssignment = {
    fightEventId: string
    playerId: string
    spellId: string

    /**
     * @deprecated Not implemented anywhere, always assign 0
     */
    eventOffset: number
}
